import type { TitleForContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { SRCatalogueKey } from '@/features/sponsored-recommendations/composables/useSendBidId';
import type { BatchImpressionTrackingOptions } from '@/helpers/composables/useBatchImpressionTracking';

import { SraEventAction } from '@/@types/graphql-types';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { getSrPlacement, SR_PROVIDER_KEY } from '@/features/sponsored-recommendations/composables/useSendBidId';

import { useBackendTracking } from './useBackendTracking';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { useBatchImpressionTracking } from '@/helpers/composables/useBatchImpressionTracking';

import { inject } from 'vue';

export interface ImpressionTrackingPosterDetails {
	title: TitleForContext;
	index?: number;
	onVisibilityCallback?: (title: TitleForContext) => void;
}

/**
 * Impression Tracking handles
 * - Sponsored Recommendation's holdout
 * - Fires when the poster is visible in the user's viewport
 * - Snowplow Title Context Graphql
 *
 * @param option: BatchImpressionTrackingOptions
 * @returns ImpressionTrackingPosterDetails
 */
export function usePosterImpressionTracking(options: BatchImpressionTrackingOptions) {
	const { trackSraEvent } = useBackendTracking();
	const srCategoryKey = inject<SRCatalogueKey | undefined>(SR_PROVIDER_KEY, undefined);

	// Called when component is visible on the screen
	function onVisibilityCallback(options: ImpressionTrackingPosterDetails) {
		options.onVisibilityCallback?.(options.title);

		if (srCategoryKey == null) return;
		const sponsoredAd = getSrPlacement(srCategoryKey);

		if (sponsoredAd?.campaign == null || sponsoredAd?.bidId == null) return;

		// Check to see if the holdout campaign matches the poster's entityId
		if (sponsoredAd?.holdoutGroup && sponsoredAd?.campaign?.node?.nodeId === options.title.id) {
			trackSraEvent({
				action: SraEventAction.Impression,
				sponsoredAd: sponsoredAd,
			});
		}
	}

	return useBatchImpressionTracking<ImpressionTrackingPosterDetails>({
		category: 'title',
		toContext: getPosterImpressionContext,
		onVisibilityCallback,
		...options,
	});
}

function getPosterImpressionContext({ title, index }: ImpressionTrackingPosterDetails): SnowplowTitleContextGraphql {
	if (typeof index === 'string') {
		captureMessageForSentry(
			`getPosterImpressionContext() - index is NaN - ${index}`,
			{ where: 'getPosterImpressionContext()' },
			'info'
		);
	}

	return SnowplowTitleContextGraphql.fromTitle(title, { index });
}
